import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import type { Organization, UpdateOrganizationRequest } from "@/api/generated/client/models";
import { normalizeWebsite, isValidWebsite } from "@/lib/form-utils";

interface OrganizationEditDialogProps {
    organization: Organization | null;
    onClose: () => void;
    updateOrganization: (args: { id: number; data: UpdateOrganizationRequest }) => Promise<unknown>;
    isUpdating: boolean;
}

export function OrganizationEditDialog({ organization, onClose, updateOrganization, isUpdating }: OrganizationEditDialogProps) {
    const [editForm, setEditForm] = useState<UpdateOrganizationRequest>({});

    useEffect(() => {
        if (organization) {
            setEditForm({
                name: organization.name,
                orgType: organization.orgType,
                phone: organization.phone,
                email: organization.email,
                address: organization.address,
                website: organization.website,
                notes: organization.notes,
            });
        }
    }, [organization]);

    const handleSave = async () => {
        if (!organization) return;
        const normalized = { ...editForm, website: normalizeWebsite(editForm.website) };
        if (!isValidWebsite(normalized.website)) return;
        try {
            await updateOrganization({ id: organization.id, data: normalized });
            onClose();
        } catch {
            // Error toast handled by mutation's onError callback
        }
    };

    return (
        <Dialog open={!!organization} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-lg max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto sm:overflow-auto">
                <DialogHeader>
                    <DialogTitle>Edit Organization</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Name <span className="text-destructive">*</span></Label>
                            <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Type</Label>
                            <Input value={editForm.orgType || ""} onChange={(e) => setEditForm({ ...editForm, orgType: e.target.value || undefined })} placeholder="e.g. Insurance Company" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value || null })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value || null })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Website</Label>
                            <Input value={editForm.website || ""} onChange={(e) => setEditForm({ ...editForm, website: e.target.value || null })} placeholder="https://..." />
                            {editForm.website && !isValidWebsite(normalizeWebsite(editForm.website)) && (
                                <p className="text-xs text-destructive">Please enter a valid URL (e.g. https://example.com)</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>Address</Label>
                            <Input value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value || null })} />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value || null })} rows={3} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={!editForm.name || isUpdating || (!!editForm.website && !isValidWebsite(normalizeWebsite(editForm.website)))}>
                        {isUpdating ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}